import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import DataTable, { TableColumn } from "react-data-table-component";
import "react-toastify/dist/ReactToastify.css";
import "../../style/customcssfile.css";
import "../../style/finishorder.css";
import { Form, Card, Row, Col, Modal } from "react-bootstrap";
import { apiAxios } from "../../utils/apiAxios";
const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface OrderTag {
  orderTagID: number;
  underlyingOrderState: string;
  dentistTag: string;
  centralHubManagerTag: string;
  centralHubDesignerTag: string;
  millingHubTag: string;
  deliveryPartnerTag: string;
  createdDate: string;
  isActive?: boolean;
  devState: string;
}

const OrderTagDetails: React.FC = () => {
  const [form, setForm] = useState({
    underlyingOrderState: "",
    dentistTag: "",
    centralHubManagerTag: "",
    centralHubDesignerTag: "",
    millingHubTag: "",
    deliveryPartnerTag: "",
    devState: "",
  });

  const [data, setData] = useState<OrderTag[]>([]);
  const [filteredData, setFilteredData] = useState<OrderTag[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const token = sessionStorage.getItem("token") || "";
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchOrderTags = async () => {
    try {
      setLoading(true);

      const res = await apiAxios.get(`${API_BASE}/OrderTags/GetAll`, {
        headers: { AuthorizationData: token },
      });

      if (res.data?.isSuccess) {
        const list = res.data.singleData.reverse();
        setData(res.data.singleData);
        setFilteredData(res.data.singleData);
      } else {
        toast.error(res.data?.status || "Failed to load Order Tags");
      }
    } catch (err) {
      toast.error("Failed to load Order Tags");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchOrderTags();
  }, []);

  useEffect(() => {
    const filtered = data.filter((d) =>
      Object.values(d)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm({
      underlyingOrderState: "",
      dentistTag: "",
      centralHubManagerTag: "",
      centralHubDesignerTag: "",
      millingHubTag: "",
      deliveryPartnerTag: "",
      devState: ""
    });
    setEditMode(false);
    setEditId(null);
  };

  const isDuplicate = () => {
    return data.some((item) => {
      if (editMode && item.orderTagID === editId) {
        return false;
      }

      return (
        item.underlyingOrderState.trim().toLowerCase() ===
        form.underlyingOrderState.trim().toLowerCase() &&
        item.dentistTag.trim().toLowerCase() ===
        form.dentistTag.trim().toLowerCase() &&
        item.centralHubManagerTag.trim().toLowerCase() ===
        form.centralHubManagerTag.trim().toLowerCase() &&
        item.centralHubDesignerTag.trim().toLowerCase() ===
        form.centralHubDesignerTag.trim().toLowerCase() &&
        item.millingHubTag.trim().toLowerCase() ===
        form.millingHubTag.trim().toLowerCase() &&
        item.deliveryPartnerTag.trim().toLowerCase() ===
        form.deliveryPartnerTag.trim().toLowerCase() &&
        item.devState.trim().toLowerCase() ===
        form.devState.trim().toLowerCase()

      );
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDuplicate()) {
      toast.error(
        editMode
          ? "Order Tag found. Update not allowed."
          : "Order Tag already exists."
      );
      return;
    }

    try {
      const payload = editMode
        ? { ...form, orderTagID: editId }
        : form;

      const url = editMode
        ? `${API_BASE}/OrderTags/Update`
        : `${API_BASE}/OrderTags/Create`;

      const method = editMode ? apiAxios.put : apiAxios.post;

      const res = await method(url, payload, {
        headers: { AuthorizationData: token },
      });

      if (res.data?.isSuccess) {
        toast.success(res.data.status);
        fetchOrderTags();
        resetForm();
      } else {
        toast.error(res.data?.status || "Operation failed");
      }
    } catch (err) {
      toast.error("Operation failed");
    }
  };


  const handleEdit = (row: OrderTag) => {
    setForm({
      underlyingOrderState: row.underlyingOrderState,
      dentistTag: row.dentistTag,
      centralHubManagerTag: row.centralHubManagerTag,
      centralHubDesignerTag: row.centralHubDesignerTag,
      millingHubTag: row.millingHubTag,
      deliveryPartnerTag: row.deliveryPartnerTag,
      devState: row.devState,    
    });
    setEditMode(true);
    setEditId(row.orderTagID);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };


  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await apiAxios.delete(
        `${API_BASE}/OrderTags/Delete/${deleteId}`,
        {
          headers: { AuthorizationData: token },
        }
      );

      if (res.data?.isSuccess) {
        toast.success(res.data.status || "Order Tag deleted");
        fetchOrderTags();
        setShowDeleteModal(false);
        setDeleteId(null);
      } else {
        toast.error(res.data?.status || "Delete failed");
      }
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const customStyles = {

    headCells: {
      style: {

        fontSize: "15px",
      },
    },
    rows: {
      style: {
        minHeight: "48px",
      },

    },

  };


  const columns: TableColumn<OrderTag>[] = [
    {
      name: "OrderTagID",
      selector: (r) => r.orderTagID ?? "-- --",
      width: "120px",
      center: true
    },
    {
      name: "Underlying Order State",
      selector: (r) => r.underlyingOrderState || "-- --",
      sortable: true,
      width: "220px",
      wrap: true
    },
    {
      name: "Dev State",
      selector: (r) => r.devState || "-- --",
      width: "160px",
      wrap: true
    },
    {
      name: "Dentist Tag",
      selector: (r) => r.dentistTag || "-- --",
      width: "160px",
      wrap: true
    },
    {
      name: "Central Hub Manager Tag",
      selector: (r) => r.centralHubManagerTag || "-- --",
      width: "210px",
      wrap: true
    },
    {
      name: "Central Hub Designer Tag",
      selector: (r) => r.centralHubDesignerTag || "-- --",
      width: "220px",
      wrap: true
    },
    {
      name: "Milling Hub Tag",
      selector: (r) => r.millingHubTag || "-- --",
      width: "160px",
      wrap: true
    },
    {
      name: "Delivery Partner Tag",
      selector: (r) => r.deliveryPartnerTag || "-- --",
      width: "180px",
      wrap: true
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            className="action-btn edit"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <i className="bi bi-pencil-square"></i>
          </button>

          <button
            className="action-btn delete"
            onClick={() => confirmDelete(row.orderTagID)}
            title="Delete"
          >
            <i className="bi bi-trash "></i>
          </button>
        </div>
      ),
      width: "110px",
      center: true
    },
  ];



  return (
    <div>
      <h5 className="mb-4 ">
        {editMode ? "Edit Order Tags" : "Add Order Tags"}
      </h5>
      <motion.div className="shadow-container rounded p-3 mb-4">


        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Underlying Order State</label>
              <input
                className="form-control"
                name="underlyingOrderState"
                value={form.underlyingOrderState}
                onChange={handleChange}
                placeholder="Enter order state"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Dev State</label>
              <input
                className="form-control"
                name="devState"
                value={form.devState}
                onChange={handleChange}
                placeholder="Enter dev state"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Dentist Tag</label>
              <input
                className="form-control"
                name="dentistTag"
                value={form.dentistTag}
                onChange={handleChange}
                placeholder="Enter dentist tag"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Central Hub Manager Tag</label>
              <input
                className="form-control"
                name="centralHubManagerTag"
                value={form.centralHubManagerTag}
                onChange={handleChange}
                placeholder="Enter manager tag"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Central Hub Designer Tag</label>
              <input
                className="form-control"
                name="centralHubDesignerTag"
                value={form.centralHubDesignerTag}
                onChange={handleChange}
                placeholder="Enter designer tag"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Milling Hub Tag</label>
              <input
                className="form-control"
                name="millingHubTag"
                value={form.millingHubTag}
                onChange={handleChange}
                placeholder="Enter milling hub tag"
              />
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label">Delivery Partner Tag</label>
              <input
                className="form-control"
                name="deliveryPartnerTag"
                value={form.deliveryPartnerTag}
                onChange={handleChange}
                placeholder="Enter delivery partner tag"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary updatebtncustom">
                  {editMode ? "Update" : "Save"}
                </button>
                {editMode && (
                  <button
                    type="button"
                    className="btn btn-secondary user-login-history-btnclear2"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </motion.div>

      <div className="shadow-container rounded p-4">
        <div className="row">
          <div className="col-md-3">

            <input
              className="form-control mb-3"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          progressPending={loading}

          noDataComponent="No order tags found"
          customStyles={customStyles}
        />
      </div>
      <Modal
        show={showDeleteModal}
        dialogClassName="custom-top-modal"
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Body className="text-center">
          <p className="mb-0 ">
            Are you sure you want to delete this Order Tag?
          </p>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <button
            className="btn btn-primary px-4"
            onClick={handleDelete}
          >
            Yes
          </button>

          <button
            className="btn btn-secondary px-4"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default OrderTagDetails;